/**
 * Minting a registration token, and the list of every token this company handed out.
 *
 * The list carries **no plaintext and cannot**: `GET /devices/registration-tokens`
 * has no `token` member on the wire — "the server stores a sha256 of it and there is
 * no field in this response for one" — and `RegistrationTokenSummary` declares none
 * either, so a row that tried to render one would not compile. What identifies a row
 * is `token_prefix`, eight characters "drawn from their own random source at mint
 * time and therefore not a prefix of the secret".
 *
 * **Dead rows are shown.** Revoked, expired and exhausted tokens are all in the list,
 * deliberately: "the question this answers is 'what did we hand out', and hiding the
 * dead rows would also hide the revocation from the person who performed it."
 *
 * `auto_approve` is offered with its three bounds stated, because the server refuses
 * the combination rather than trusting it: an `employee_id` must be present,
 * `max_uses` must be 1, and the lifetime must be at most 86400 seconds. A screen that
 * offered the checkbox without the sentence would produce a `400` the operator cannot
 * interpret.
 */

import { useState, type ReactNode, type SyntheticEvent } from 'react';

import { useI18n } from '../../i18n';
import { formatInstant } from '../../i18n/datetime';
import type { RegistrationTokenCreateRequest, RegistrationTokenSummary } from '../../api/types';
import { EmployeePicker, type EmployeePickerValue } from '../../components/EmployeePicker';
import { Badge } from '../../states/Badge';
import type { IconName, Severity } from '../../states/icons';
import type { MessageKey } from '../../i18n/messages';

/** The one unit conversion on this screen: the box asks for hours, the contract takes seconds. */
const SECONDS_PER_HOUR = 3600;

/**
 * Three states, from fields the row already carries — no clock needed for two of
 * them, and the third is left to the server's own `expires_at` rendered beside it.
 *
 * "Expired" is deliberately **not** computed here. It would need `Date.now()` in a
 * browser, and a laptop with a wrong clock would then report a live token as dead;
 * the expiry timestamp is printed instead and the reader compares it themselves. Task
 * 44 took the same ruling about data freshness for the same reason.
 */
function tokenState(row: RegistrationTokenSummary): {
  key: MessageKey;
  severity: Severity;
  icon: IconName;
} {
  if (row.revoked_at !== null) {
    return { key: 'devices.tokens.state.revoked', severity: 'info', icon: 'ban' };
  }
  if (row.used_count >= row.max_uses) {
    return { key: 'devices.tokens.state.exhausted', severity: 'info', icon: 'square' };
  }
  return { key: 'devices.tokens.state.usable', severity: 'low', icon: 'circle-check' };
}

export interface TokenPanelProps {
  readonly tokens: readonly RegistrationTokenSummary[];
  readonly timezone: string;
  readonly creating: boolean;
  readonly onCreate: (body: RegistrationTokenCreateRequest) => void;
  readonly onRevokeToken: (tokenId: string, reason: string) => void;
  /** The reveal, the create error and the revoke outcome, all owned by the screen. */
  readonly revealSlot?: ReactNode;
  readonly createOutcome?: ReactNode;
  readonly revokeOutcome?: ReactNode;
}

export function TokenPanel({
  tokens,
  timezone,
  creating,
  onCreate,
  onRevokeToken,
  revealSlot,
  createOutcome,
  revokeOutcome,
}: TokenPanelProps): ReactNode {
  const { t, locale } = useI18n();
  // Hours on the paper, seconds on the wire. `expires_in_seconds` is what the contract
  // takes and 86400 is what it caps at — but 86400 is a number nobody reads as a day,
  // and a registration slip is a thing an IT person hands over "good until tomorrow"
  // (finish review, minor d). The box asks for hours; `submit` multiplies.
  const [expiresHours, setExpiresHours] = useState('24');
  const [maxUses, setMaxUses] = useState('1');
  const [employee, setEmployee] = useState<EmployeePickerValue | null>(null);
  const [note, setNote] = useState('');
  const [autoApprove, setAutoApprove] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [revokeReason, setRevokeReason] = useState('');

  const submit = (event: SyntheticEvent): void => {
    event.preventDefault();
    const employeeId = employee?.achau_user_id ?? '';
    const label = note.trim();
    onCreate({
      expires_in_seconds: Math.round(Number(expiresHours) * SECONDS_PER_HOUR),
      max_uses: Number(maxUses),
      auto_approve: autoApprove,
      ...(employeeId === '' ? {} : { employee_id: employeeId }),
      ...(label === '' ? {} : { note: label }),
    });
  };

  const submitRevoke = (event: SyntheticEvent, tokenId: string): void => {
    event.preventDefault();
    if (revokeReason.trim() === '') return;
    onRevokeToken(tokenId, revokeReason.trim());
    setRevokingId(null);
    setRevokeReason('');
  };

  return (
    <section className="wp-section" aria-labelledby="k5-tokens-heading" data-testid="k5-tokens">
      <h2 className="wp-section__title" id="k5-tokens-heading">
        {t('devices.tokens.title')}
      </h2>
      <p className="wp-small wp-muted">{t('devices.tokens.intro')}</p>

      {revealSlot}

      <form onSubmit={submit} className="wp-c-form" data-testid="k5-token-form">
        <div className="wp-c-field-pair">
          <label className="wp-field" htmlFor="k5-token-expires">
            <span className="wp-field__label">{t('devices.tokens.expires')}</span>
            <input
              id="k5-token-expires"
              className="wp-field__input"
              inputMode="numeric"
              value={expiresHours}
              aria-describedby="k5-token-expires-hint"
              onChange={(event) => {
                setExpiresHours(event.target.value);
              }}
              data-testid="k5-token-expires"
            />
            <span className="wp-small wp-muted" id="k5-token-expires-hint">
              {t('devices.tokens.expiresHint')}
            </span>
          </label>
          <label className="wp-field" htmlFor="k5-token-uses">
            <span className="wp-field__label">{t('devices.tokens.maxUses')}</span>
            <input
              id="k5-token-uses"
              className="wp-field__input"
              inputMode="numeric"
              value={maxUses}
              onChange={(event) => {
                setMaxUses(event.target.value);
              }}
              data-testid="k5-token-uses"
            />
          </label>
        </div>
        <div className="wp-c-field-pair">
          {/* Was "Gắn sẵn mã nhân sự achau (tuỳ chọn)" over an empty numeric box —
              a machine id asked of a human being (Luật Không In Mã Máy, R3). The
              endpoint still takes the achau id; the picker is what resolves a name
              into one, so nobody has to know it. */}
          <div className="wp-field">
            <EmployeePicker
              label={t('devices.tokens.employee')}
              testId="k5-token-employee"
              value={employee}
              onChange={setEmployee}
            />
            <span className="wp-small wp-muted">{t('devices.employeePickHint')}</span>
          </div>
          <label className="wp-field" htmlFor="k5-token-note">
            <span className="wp-field__label">{t('devices.tokens.note')}</span>
            <input
              id="k5-token-note"
              className="wp-field__input"
              value={note}
              onChange={(event) => {
                setNote(event.target.value);
              }}
              data-testid="k5-token-note"
            />
          </label>
        </div>
        <div className="wp-c-field-pair">
          <label className="wp-toggle" htmlFor="k5-token-auto">
            <input
              id="k5-token-auto"
              type="checkbox"
              checked={autoApprove}
              aria-describedby="k5-token-auto-hint"
              onChange={(event) => {
                setAutoApprove(event.target.checked);
              }}
              data-testid="k5-token-auto"
            />
            <span>{t('devices.tokens.autoApprove')}</span>
          </label>
          <p className="wp-small wp-muted" id="k5-token-auto-hint">
            {t('devices.tokens.autoApproveHelp')}
          </p>
        </div>
        <div className="wp-c-actions">
          <button
            type="submit"
            className="wp-button wp-button--primary"
            data-testid="k5-token-create"
          >
            {creating ? t('devices.tokens.creating') : t('devices.tokens.create')}
          </button>
        </div>
      </form>
      {createOutcome}

      <p className="wp-small wp-muted">{t('devices.tokens.deadRows')}</p>
      {tokens.length === 0 ? (
        <p className="wp-muted" data-testid="k5-token-empty">
          {t('devices.tokens.empty')}
        </p>
      ) : (
        <div className="wp-table-scroll">
          <table className="wp-table" data-testid="k5-token-table">
            <thead>
              <tr>
                <th scope="col">{t('devices.tokens.col.prefix')}</th>
                <th scope="col">{t('devices.tokens.col.label')}</th>
                <th scope="col">{t('devices.tokens.col.expires')}</th>
                <th className="wp-num" scope="col">
                  {t('devices.tokens.col.uses')}
                </th>
                <th scope="col">{t('devices.tokens.col.state')}</th>
                <th scope="col">{t('devices.tokens.col.action')}</th>
              </tr>
            </thead>
            <tbody>
              {tokens.map((row) => {
                const state = tokenState(row);
                return (
                  <tr key={row.token_id} data-testid={`k5-token-row-${row.token_id}`}>
                    <th scope="row" className="wp-mono" title={t('devices.tokens.prefixHelp')}>
                      {row.token_prefix}
                    </th>
                    <td>{row.label ?? ''}</td>
                    <td>{formatInstant(row.expires_at, locale, timezone)}</td>
                    <td className="wp-num">
                      {t('devices.tokens.uses', { used: row.used_count, max: row.max_uses })}
                    </td>
                    <td>
                      <Badge
                        severity={state.severity}
                        icon={state.icon}
                        label={t(state.key)}
                        testId={`k5-token-state-${row.token_id}`}
                      />
                      {row.revoke_reason === null ? null : (
                        <p className="wp-small wp-muted">{row.revoke_reason}</p>
                      )}
                    </td>
                    <td>
                      {row.revoked_at !== null ? null : revokingId === row.token_id ? (
                        <form
                          onSubmit={(event) => {
                            submitRevoke(event, row.token_id);
                          }}
                        >
                          <label className="wp-field" htmlFor={`k5-token-reason-${row.token_id}`}>
                            <span className="wp-field__label">
                              {t('devices.tokens.revokeReason', { prefix: row.token_prefix })}
                            </span>
                            <input
                              id={`k5-token-reason-${row.token_id}`}
                              className="wp-field__input"
                              value={revokeReason}
                              onChange={(event) => {
                                setRevokeReason(event.target.value);
                              }}
                              data-testid={`k5-token-reason-${row.token_id}`}
                            />
                          </label>
                          <button
                            type="submit"
                            className="wp-button"
                            data-testid={`k5-token-revoke-submit-${row.token_id}`}
                          >
                            {t('devices.tokens.revokeSubmit')}
                          </button>
                          <p className="wp-small wp-muted">{t('devices.tokens.revokeUsedNote')}</p>
                        </form>
                      ) : (
                        <button
                          type="button"
                          className="wp-button wp-button--quiet"
                          onClick={() => {
                            setRevokingId(row.token_id);
                            setRevokeReason('');
                          }}
                          data-testid={`k5-token-revoke-${row.token_id}`}
                        >
                          {t('devices.tokens.revoke')}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {revokeOutcome}
    </section>
  );
}
